import {
  Button,
  Flex,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@aws-amplify/ui-react"
import {
  DndContext,
  closestCenter,
  useSensors,
  useSensor,
  PointerSensor,
} from "@dnd-kit/core"
import type { DragEndEvent } from "@dnd-kit/core"
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers"
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import {
  RecipeComponentSchemaMassKgMin,
  RecipeComponentSchemaMixingTimeMinutesMax,
  RecipeCreateSchema,
  RecipeSchemaNameMaxLength,
  RecipeUpdateSchema,
} from "@strtmn/sds/schemas"
import {
  type RecipeDto,
  type RecipeComponent,
  type RecipeCreateDto,
  type RecipePortionDto,
  type RecipeUpdateDto,
  type RecipesAusladegruppen,
  type ResponseMessage,
  type OrganizationDto,
} from "@strtmn/sds/types"
import { getErrorMessage } from "@strtmn/strtmn"
import { Cache, I18n } from "aws-amplify/utils"
import { useContext, useEffect, useState } from "react"
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
  type FieldArrayWithId,
  type FieldErrors,
  type FieldValues,
  type SubmitHandler,
  type UseFieldArrayRemove,
  type UseFieldArrayUpdate,
} from "react-hook-form"
import { FaGripLines, FaRegTrashCan } from "react-icons/fa6"
import { useNavigate, useParams } from "react-router-dom"
import { delToApi, postToApi, putToApi } from "../../../api/utils"
import { CancelButton, Modal } from "../../../components"
import config, { isProd } from "../../../config"
import { UserDataContext, UserDataContextHelper } from "../../../contexts"
import { useIsReadOnly } from "../../../hooks"
import { getLinkToOrgRecipes } from "../../../LinkUtils"
import { UNIT_OF_MASS } from "../../../org-settings"
import { imageHash } from "../-components/ListItem"
import { transformInputToNumber, transformNumberToString } from "./RecipeUtils"

import "../RecipeManagement.css"

const NUM_RC_IMAGES = config.images.rc.numImages
const IMAGE_HASH_SEED = config.images.rc.imageHashSeed

type ComponentModes = "create" | "edit" | "invalid"

/** Cleans the form data before submission. */
export function cleanData(data: RecipeDto, isCreateMode: boolean): RecipeDto {
  console.debug("▶️ cleanData")
  const cleaned: RecipeDto = JSON.parse(JSON.stringify(data))
  /** @todo: there could be extra properties -- how to deal with them? */
  /** @todo: What about validation? */

  if (isCreateMode) {
    // Delete the Recipe's id property. Filled by the backend.
    /** @ts-ignore */
    delete cleaned.id
  }

  cleaned.RecipeComponents.forEach((elem) => {
    // Mixing time in the backend and DTO is in seconds, but this form uses minutes.
    if (elem.mixing_time_s) {
      elem.mixing_time_s = Math.round(elem.mixing_time_s * 60)
    }
  })

  if (!isCreateMode) {
    const recipeId = cleaned.id
    // console.log("Recipe ID = ", recipeId)
    cleaned.RecipesAusladegruppen.forEach((elem) => {
      elem.recipe_id = recipeId
    })
    cleaned.RecipeComponents.forEach((elem) => {
      elem.recipe_id = recipeId
    })
  }

  if (!cleaned.portions || cleaned.portions.length === 0) {
    cleaned.portions = null
  } else {
    console.log(
      "⚡ NOT deleting portion ids - they used to get deleted, but why? If they are invalid, the BE assigns a random uuid."
    )
    // console.log("⚡⚡⚡ deleting portion ids -- but why??")
    // cleaned.portions.forEach((portion) => {
    //   console.log("portion.id before removal:", portion.id)
    //   if (!isValidUUID)
    //   delete (portion as any).id
    // })
  }
  return cleaned
}

const getEmptyRecipe = (org: OrganizationDto): RecipeDto => {
  return {
    id: "",
    org_id: org.id,
    name: "",
    created_at: "",
    created_by: "",
    updated_at: "",
    updated_by: "",
    portions: null,
    RecipeComponents: [getEmptyRecipeComponent()],
    RecipesAusladegruppen: [getEmptyAusladegruppe()],
  }
}
const getEmptyRecipeComponent = (order: number = 1): RecipeComponent => {
  return {
    order: order,
    feed_component_id: "",
    mass_kg: 0,
    mixing_time_s: 0 * 60,
    recipe_id: "",
  }
}
const getEmptyAusladegruppe = (order: number = 1): RecipesAusladegruppen => {
  return {
    order: order,
    ausladegruppe_id: "",
    recipe_id: "",
  }
}

interface RecipeAusladegruppenProps {
  org: OrganizationDto
}
function RecipeAusladegruppen({ org }: RecipeAusladegruppenProps) {
  const { fields, append, remove, move } = useFieldArray({
    name: "RecipesAusladegruppen",
  })

  // drag-and-drop
  const modifiers = [restrictToVerticalAxis, restrictToParentElement]
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6,
      },
    })
  )
  const handleDragDrop = async (event: DragEndEvent) => {
    console.log("fields:", fields)
    const { active, over } = event
    if (!over || active.id === over?.id) {
      return
    }
    const startLinkIndex = fields.findIndex((item) => item.id === active.id)
    const dropLinkIndex = fields.findIndex((item) => item.id === over?.id)
    move(startLinkIndex, dropLinkIndex)
  }

  let isAddDisabled =
    fields.length >= config.limits.numberOfAnimalGroupsPerRecipe
  return (
    <>
      <h3 className="!font-semibold">Ausladegruppen</h3>
      <div className="overflow-x-auto">
        <Table className="recipe-ausladegruppen" size="small">
          <TableHead>
            <TableRow>
              <TableCell as="th">{/* Rhf. */}</TableCell>
              <TableCell as="th">Name</TableCell>
              <TableCell as="th">Anz. Tiere</TableCell>
              <TableCell as="th">{/* Buttons */}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <DndContext
              collisionDetection={closestCenter}
              onDragEnd={handleDragDrop}
              modifiers={modifiers}
              sensors={sensors}
            >
              <SortableContext
                items={fields}
                strategy={verticalListSortingStrategy}
              >
                {fields.map((ra, index) => (
                  <RecipeAusladegruppenEntry
                    key={ra.id}
                    org={org}
                    ra={ra}
                    index={index}
                    remove={remove}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </TableBody>
        </Table>
      </div>
      {/* Render the "Add" button */}
      <div className="mt-4">
        <Button
          isDisabled={isAddDisabled}
          onClick={() => {
            append(getEmptyAusladegruppe(fields.length + 1))
          }}
        >
          Ausladegruppe hinzufügen
        </Button>
      </div>
    </>
  )
}

interface RecipeAusladegruppenEntryProps {
  org: OrganizationDto
  ra: Record<"id", string>
  index: number
  remove: UseFieldArrayRemove
}
function RecipeAusladegruppenEntry({
  org,
  ra,
  index,
  remove,
}: RecipeAusladegruppenEntryProps) {
  const {
    register,
    formState: { errors },
  } = useFormContext()
  const defaultAg = { number_of_animals: -1 }

  const [agId, setAgId] = useState<string | undefined>(
    (ra as any).ausladegruppe_id as string | undefined
  )
  const [ag, setAg] = useState<{ number_of_animals: number }>(defaultAg)

  useEffect(() => {
    // console.log("effect runs")
    const newAg = agId
      ? UserDataContextHelper.findAnimalGroup(org, agId)
      : defaultAg
    setAg(newAg)
    // console.log("ag <-", agx)
  }, [agId /*, defaultAg*/, org])
  // console.log("entry", order, "renders, num =", ag.number_of_animals)

  // drag-and-drop
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: ra.id })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <>
      <TableRow key={ra.id} style={style} ref={setNodeRef} {...attributes}>
        <TableCell>
          <FaGripLines className="inline" {...listeners} />
        </TableCell>
        <TableCell>
          <select
            {...register(`RecipesAusladegruppen.${index}.ausladegruppe_id`, {
              required: "Wählen Sie eine Ausladegruppe aus.",
              onChange: (event) => {
                setAgId(event.target.value)
                // console.log("onChange, agId <-", event.target.value)
              },
            })}
            className={
              errors["RecipesAusladegruppen"] &&
              /** @ts-ignore */
              errors.RecipesAusladegruppen[index] &&
              /** @ts-ignore */
              errors.RecipesAusladegruppen[index]?.ausladegruppe_id
                ? "error"
                : ""
            }
          >
            <option key="" value="" disabled>
              Auswählen...
            </option>
            {org.Ausladegruppen.sort((a, b) =>
              a.name.localeCompare(b.name, undefined, {
                sensitivity: "base",
                numeric: true,
              })
            ).map((ag) => (
              <option key={ag.id} value={ag.id}>
                {ag.name}
              </option>
            ))}
          </select>
        </TableCell>
        <TableCell className="anz">
          {ag.number_of_animals < 0 ? "" : ag.number_of_animals}
        </TableCell>
        <TableCell>
          <Button
            variation="warning"
            className="!border-none"
            onClick={() => remove(index)}
          >
            <span role="img" aria-label="remove">
              <FaRegTrashCan />
            </span>
          </Button>
        </TableCell>
      </TableRow>
      {errors["RecipesAusladegruppen"] &&
        (errors["RecipesAusladegruppen"] as any)[index] && (
          <TableRow className="error">
            <TableCell colSpan={5}>
              {Object.keys((errors["RecipesAusladegruppen"] as any)[index]).map(
                (fieldName) => (
                  <div key={ra.id + "-errors-" + fieldName}>
                    Fehler:{" "}
                    {
                      (errors["RecipesAusladegruppen"] as any)[index][fieldName]
                        .message
                    }
                  </div>
                )
              )}
            </TableCell>
          </TableRow>
        )}
    </>
  )
}

interface RecipeComponentsListProps {
  org: OrganizationDto
}
function RecipeComponentsList({ org }: RecipeComponentsListProps) {
  const { fields, append, remove, move, update } = useFieldArray({
    name: "RecipeComponents",
  })

  // drag-and-drop
  const modifiers = [restrictToVerticalAxis, restrictToParentElement]
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6,
      },
    })
  )
  const handleDragDrop = async (event: DragEndEvent) => {
    console.log("fields:", fields)
    const { active, over } = event
    if (!over || active.id === over?.id) {
      return
    }
    const startLinkIndex = fields.findIndex((item) => item.id === active.id)
    const dropLinkIndex = fields.findIndex((item) => item.id === over?.id)
    move(startLinkIndex, dropLinkIndex)
  }

  // fields.forEach((f, idx) => console.log(`🪅🪅🪅 field ${idx} = `, f))

  let isAddDisabled =
    fields.length >= config.limits.numberOfFeedComponentsPerRecipe
  return (
    <>
      <h3 className="!font-semibold">Komponenten</h3>
      <div className="overflow-x-auto">
        <Table className="recipe-components" size="small">
          <TableHead>
            <TableRow>
              <TableCell as="th">{/* Rhf. */}</TableCell>
              <TableCell as="th">Komponente</TableCell>
              <TableCell as="th">
                Trockenmasse
                <br />
                pro Tier
              </TableCell>
              <TableCell as="th">
                Frischmasse
                <br />
                pro Tier
              </TableCell>
              <TableCell as="th">
                Nachmischzeit
                <br />
                Minuten
              </TableCell>
              <TableCell as="th">{/* Buttons */}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <DndContext
              collisionDetection={closestCenter}
              onDragEnd={handleDragDrop}
              modifiers={modifiers}
              sensors={sensors}
            >
              <SortableContext
                items={fields}
                strategy={verticalListSortingStrategy}
              >
                {/* Render components from the recipe */}
                {fields.map((rc, index) => (
                  <RecipeComponentsEntry
                    key={rc.id}
                    org={org}
                    rc={rc}
                    index={index}
                    remove={remove}
                    update={update}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </TableBody>
        </Table>
      </div>
      {/* Render the "Add" button */}
      <div className="mt-4">
        <Button
          isDisabled={isAddDisabled}
          onClick={() => {
            const newComponentData = getEmptyRecipeComponent(fields.length + 1)
            // console.log("🪅 newComponentData = ", newComponentData)
            append(newComponentData, { shouldFocus: true })
            // console.log("🪅 last field = ", fields[fields.length - 1])
          }}
        >
          Komponente hinzufügen
        </Button>
      </div>
    </>
  )
}

interface RecipeComponentsEntryProps {
  org: OrganizationDto
  rc: Record<"id", string>
  index: number
  remove: UseFieldArrayRemove
  update: UseFieldArrayUpdate<FieldValues, "RecipeComponents">
}
function RecipeComponentsEntry({
  org,
  rc,
  index,
  remove,
  update,
}: RecipeComponentsEntryProps) {
  const {
    register,
    control,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext()

  // const field_fcId = (rc as any)["feed_component_id"]
  // 0 === index && console.log("field:fc_id", field_fcId)

  const [fcId, setFcId] = useState<string | undefined>(undefined)
  const [originalField, setOriginalField] = useState<any>(undefined)
  // Buffer the dry-/fresh-mass inputs' raw text while focused, so the
  // live-computed display doesn't fight the user's keystrokes. `null` means
  // "not currently editing" - display falls back to the computed value.
  const [dryMassDraft, setDryMassDraft] = useState<string | null>(null)
  const [freshMassDraft, setFreshMassDraft] = useState<string | null>(null)

  // drag-and-drop
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: rc.id })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  /**
   * @todo: This does not work, as the component looses its state.
   * We need to preserve the original state outside the component, and pass it as a parameter.
   */
  if (undefined === originalField) {
    // 0 === index && console.log("🎉 memo-ing original field")
    setOriginalField(rc)
  } else {
    // 0 === index && console.log(`🎉 (${index}) GOT OLREADY original field`, originalField)
  }

  const availableFeedComponents = org.FeedComponents
  const hasUserSelection = undefined !== fcId

  const onSelectedChange = (event: any) => {
    const selectedFcId = event.target.value
    // console.log("🎉 fcId <-", selectedFcId)
    setFcId(selectedFcId)
  }

  // if (0 === index) {
  //   // console.log("errors", errors)
  //   console.log(`🎉 (${index}) hasUserSelection`, hasUserSelection)
  //   console.log(`🎉 (${index}) rc`, rc)
  //   // console.log("🎉 fcId", fcId)
  //   // console.log("🎉 fc", availableFeedComponents.find(item => item.id === (rc as any)["feed_component_id"]))
  //   console.log("rc.id", rc.id)
  //   console.log("rc:fc_id", (rc as any)["feed_component_id"])
  //   console.log("availableFeedComponents", availableFeedComponents)
  //   // console.log("_fields", control._fields)
  //   // console.log("_fields.RecipeComponents", control._fields.RecipeComponents)
  //   // const rcs = ensure(control._fields.RecipeComponents)
  //   // // const my_field: any = ((control._fields.RecipeComponents[index]) as any)
  //   // console.log("field rcs", rcs)
  //   console.log("🎉 fcId", fcId)
  //   console.log("🎉 field_fcId", field_fcId)
  // }

  let selectedFc = availableFeedComponents.find((fc) => fc.id === fcId)
  if (!selectedFc) {
    selectedFc = availableFeedComponents.find(
      (fc) => fc.id === originalField?.feed_component_id
    )
  }
  if (!selectedFc) {
    selectedFc = availableFeedComponents.find(
      (fc) => fc.id === (rc as any)?.feed_component_id
    )
  }
  // if (!selectedFc) {
  //   return <TableRow>
  //     <TableCell>No FC selected</TableCell>
  //   </TableRow>
  // }
  // 0 === index && console.log("selectedFc", selectedFc)

  // Has the user selected a FeedComponent?
  /** @todo DOES THIS WORK FOR NEW LINES?? */
  if (hasUserSelection) {
    // console.log(`🎉 (${index}) There is a user-selected fc: ${fcId}`)
    if (fcId === (originalField as any)?.feed_component_id) {
      // if (0 === index) {
      //   console.log(`🎉 (${index}) Original Fc Id selected. Resetting row (mass, etc.) to original values.`)
      //   console.log(`🎉 (${index}) Original FcId:`, originalField.feed_component_id)
      //   console.log(`🎉 (${index}) Original mass_kg:`, originalField.mass_kg)
      //   console.log(`🎉 (${index}) Original ts_gehalt_percent:`, originalField.ts_gehalt_percent)
      // }
      // console.log("🪅🪅🪅 originalField = ", originalField)
      update(index, originalField)
      // resetField("mass_kg")
      // resetField("ts_gehalt_percent")

      // Reset the user selection
      setFcId(undefined)
    } else {
      // 0 === index && console.log(`🎉 (${index}) Different Fc Id selected. Resetting row (mass, etc.) to nil.`)
      // (control._fields.RecipeComponents as any)["mass_kg"] = 0
      const newFieldData = {
        ...rc,
        feed_component_id: selectedFc?.id,
        mass_kg: 0,
        // ts_gehalt_percent: selectedFc?.ts_gehalt_percent
      }
      // console.log("🪅🪅🪅 newFieldData = ", newFieldData)
      update(index, newFieldData)
      // setValue("mass_kg", 0)
      // setValue("ts_gehalt_percent", selectedFc.ts_gehalt_percent)
    }
  }

  // console.log("errors", errors)

  const tsGehaltPercent = Number(selectedFc?.ts_gehalt_percent) || 0
  const isDryMassEditable = tsGehaltPercent > 0
  const massKgWatched = Number(watch(`RecipeComponents.${index}.mass_kg`)) || 0
  const computedFreshMassKg = isDryMassEditable
    ? massKgWatched * (100 / tsGehaltPercent)
    : massKgWatched

  return (
    <>
      <TableRow key={rc.id} style={style} ref={setNodeRef} {...attributes}>
        <TableCell>
          <FaGripLines className="inline" {...listeners} />
        </TableCell>
        <TableCell>
          <select
            {...register(`RecipeComponents.${index}.feed_component_id`, {
              required: "Wählen Sie eine Komponente aus.",
              onChange: onSelectedChange,
            })}
            className={
              errors["RecipeComponents"] &&
              /** @ts-ignore */
              errors.RecipeComponents[index] &&
              /** @ts-ignore */
              errors.RecipeComponents[index]?.feed_component_id
                ? "error"
                : ""
            }
          >
            <option key="" value="" disabled>
              Auswählen...
            </option>
            {availableFeedComponents
              .sort((a, b) =>
                a.name.localeCompare(b.name, undefined, {
                  sensitivity: "base",
                  numeric: true,
                })
              )
              .map((fc) => (
                <option key={fc.id} value={fc.id}>
                  {fc.name}
                </option>
              ))}
          </select>
        </TableCell>
        <TableCell className="fm">
          <Controller
            control={control}
            name={`RecipeComponents.${index}.mass_kg`}
            render={({ field }) => (
              <input
                {...field}
                disabled={!isDryMassEditable}
                className={
                  "w-20" +
                  (errors["RecipeComponents"] &&
                  /** @ts-ignore */
                  errors.RecipeComponents[index] &&
                  /** @ts-ignore */
                  errors.RecipeComponents[index]?.mass_kg
                    ? " error"
                    : "")
                }
                value={
                  dryMassDraft !== null
                    ? dryMassDraft
                    : transformNumberToString(field.value)
                }
                onChange={(e) => setDryMassDraft(e.target.value)}
                onFocus={() =>
                  setDryMassDraft(transformNumberToString(field.value))
                }
                onBlur={(e) => {
                  field.onChange(transformInputToNumber(e.target.value))
                  field.onBlur()
                  setDryMassDraft(null)
                }}
              />
            )}
            rules={{
              min: {
                value: RecipeComponentSchemaMassKgMin,
                message: `Die Masse muss mindestens ${RecipeComponentSchemaMassKgMin} ${UNIT_OF_MASS} sein.`,
              },
            }}
          />{" "}
          {UNIT_OF_MASS}
        </TableCell>
        <TableCell className="dm">
          <input
            className="w-20"
            value={
              freshMassDraft !== null
                ? freshMassDraft
                : transformNumberToString(computedFreshMassKg)
            }
            onChange={(e) => setFreshMassDraft(e.target.value)}
            onFocus={() =>
              setFreshMassDraft(transformNumberToString(computedFreshMassKg))
            }
            onBlur={(e) => {
              const freshMassKg = transformInputToNumber(e.target.value)
              const newMassKg = isDryMassEditable
                ? freshMassKg * (tsGehaltPercent / 100)
                : freshMassKg
              setValue(`RecipeComponents.${index}.mass_kg`, newMassKg, {
                shouldDirty: true,
              })
              setFreshMassDraft(null)
            }}
          />{" "}
          {UNIT_OF_MASS}
        </TableCell>
        <TableCell className="mt">
          <Controller
            control={control}
            name={`RecipeComponents.${index}.mixing_time_s`}
            render={({ field }) => (
              <input
                {...field}
                className={
                  "w-12" +
                  (errors["RecipeComponents"] &&
                  /** @ts-ignore */
                  errors.RecipeComponents[index] &&
                  /** @ts-ignore */
                  errors.RecipeComponents[index]?.mixing_time_s
                    ? " error"
                    : "")
                }
                onBlur={(e) => {
                  // field.onChange(transformInputToSeconds(e.target.value))
                  field.onChange(transformInputToNumber(e.target.value))
                  field.onBlur()
                }}
                // value={transformSecondsToMinutes(field.value)}
                value={transformNumberToString(field.value)}
              />
            )}
            rules={{
              required: false,
              min: {
                value: 0,
                message: "Die Mischzeit darf nicht negativ sein.",
              },
              max: {
                value: RecipeComponentSchemaMixingTimeMinutesMax,
                message: `Die Mischzeit darf maximal ${RecipeComponentSchemaMixingTimeMinutesMax} Minuten sein.`,
              },
            }}
          />
          {/** @todo allow fractional input */}
        </TableCell>
        <TableCell>
          <Button
            variation="warning"
            className="!border-none"
            onClick={() => {
              remove(index)
            }}
          >
            <span role="img" aria-label="remove">
              <FaRegTrashCan />
            </span>
          </Button>
        </TableCell>
      </TableRow>
      {errors["RecipeComponents"] &&
        (errors["RecipeComponents"] as any)[index] && (
          <TableRow className="error">
            <TableCell colSpan={6}>
              {Object.keys((errors["RecipeComponents"] as any)[index]).map(
                (fieldName) => (
                  <div key={rc.id + "-errors-" + fieldName}>
                    Fehler:{" "}
                    {
                      (errors["RecipeComponents"] as any)[index][fieldName]
                        .message
                    }
                  </div>
                )
              )}
            </TableCell>
          </TableRow>
        )}
    </>
  )
}

interface RecipePortionsProps {
  org: OrganizationDto
  parentRecipeName: string
}
function RecipePortions({ org, parentRecipeName }: RecipePortionsProps) {
  const {
    control,
    // formState: { errors },
    watch,
  } = useFormContext<RecipeDto>()
  // useFieldArray by default assigns a random .id property.
  // We already use an .id property (the portion's id), so we must use a different keyName.
  // And we need this type to tell the compiler that our objects do have an .id property.
  type PortionFieldArrayWithId = FieldArrayWithId<
    RecipeDto,
    "portions",
    "fieldKey"
  > &
    Record<"id", string>
  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "portions",
    keyName: "fieldKey",
  })

  // Watch recipe components to calculate total mass
  const recipeComponents = watch("RecipeComponents") || []
  const portions = watch("portions") || []

  const totalPercentage = portions.reduce((sum, portion) => {
    const value = Number(portion.percentage) || 0
    return sum + (Number.isNaN(value) ? 0 : value)
  }, 0)

  const totalComponentMass = recipeComponents.reduce(
    (sum, component) => sum + (component.mass_kg || 0),
    0
  )
  const totalComponentFreshMass = recipeComponents.reduce((sum, component) => {
    if (!component?.feed_component_id) {
      return sum
    }
    const fc = UserDataContextHelper.findFeedComponent(
      org,
      component.feed_component_id
    )
    const mass_kg = component.mass_kg || 0
    const freshMass_kg =
      Number(fc.ts_gehalt_percent) > 0
        ? mass_kg *
          /** @ts-ignore */
          (100 / fc.ts_gehalt_percent)
        : mass_kg
    return sum + freshMass_kg
  }, 0)
  const totalMassPerAnimal = portions.reduce(
    (sum, portion) =>
      sum + ((portion.percentage || 0) / 100) * totalComponentMass,
    0
  )
  const totalFreshMassPerAnimal = portions.reduce(
    (sum, portion) =>
      sum + ((portion.percentage || 0) / 100) * totalComponentFreshMass,
    0
  )

  const modifiers = [restrictToVerticalAxis, restrictToParentElement]
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6,
      },
    })
  )
  const handleDragDrop = async (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over?.id) {
      return
    }
    const startIndex = fields.findIndex((item) => item.id === active.id)
    const dropIndex = fields.findIndex((item) => item.id === over?.id)

    // Create a new array with updated order values
    const updatedFields = [...fields]
    const [movedItem] = updatedFields.splice(startIndex, 1)
    if (movedItem) {
      updatedFields.splice(dropIndex, 0, movedItem)
    }

    // Update the order property for all items based on their new positions
    updatedFields.forEach((field, index) => {
      if (field.order !== index + 1) {
        update(index, { ...field, order: index + 1 })
      }
    })
  }

  const handleAddInitialPortions = () => {
    const timestamp = Date.now()
    append([
      {
        id: `temp-${timestamp}-1`,
        name: `${parentRecipeName}_1`,
        percentage: 50,
        order: 1,
      },
      {
        id: `temp-${timestamp}-2`,
        name: `${parentRecipeName}_2`,
        percentage: 50,
        order: 2,
      },
    ])
  }

  /** @todo This could be computed: Limit - Total Number Of Portions - Total Number Of Recipes Without Portions */
  const isAddDisabled = fields.length >= config.limits.numberOfRecipes - 1

  const sortedFields = [...fields].sort(
    (a, b) => (a.order || 0) - (b.order || 0)
  )
  return (
    <>
      <h3 className="!font-semibold">Portionen</h3>
      {fields.length === 0 ? (
        <div className="mt-4">
          <div>Dieses Rezept ist nicht in Portionen aufgeteilt.</div>
          <Button className="!mt-4" onClick={handleAddInitialPortions}>
            In Portionen aufteilen
          </Button>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <Table className="recipe-portions" size="small">
              <TableHead>
                <TableRow>
                  <TableCell as="th">{/* Drag handle */}</TableCell>
                  <TableCell as="th">Name</TableCell>
                  <TableCell as="th">Prozentsatz</TableCell>
                  <TableCell as="th">{/* Buttons */}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <DndContext
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragDrop}
                  modifiers={modifiers}
                  sensors={sensors}
                >
                  <SortableContext
                    items={fields as PortionFieldArrayWithId[]}
                    strategy={verticalListSortingStrategy}
                  >
                    {sortedFields.map((portion) => (
                      <RecipePortionsEntry
                        key={portion.fieldKey}
                        org={org}
                        portion={portion as PortionFieldArrayWithId}
                        index={fields.findIndex(
                          (item) => item.id === portion.id
                        )} // Use original index
                        remove={remove}
                        parentRecipeName={parentRecipeName}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              </TableBody>
            </Table>
          </div>
          <div className="mt-4">
            <Button
              isDisabled={isAddDisabled}
              onClick={() => {
                const order = fields.length + 1
                append({
                  id: `temp-${Date.now()}`,
                  name: `${parentRecipeName}_${order}`,
                  percentage: 0,
                  order: order,
                })
              }}
            >
              Portion hinzufügen
            </Button>
          </div>
          <div className="mt-2 counters">
            <div>
              Die Portionen ergeben insg.{" "}
              {totalPercentage.toLocaleString(undefined, {
                maximumFractionDigits: 2,
              })}{" "}
              %.
            </div>
            <div>
              Das macht insg.{" "}
              {totalMassPerAnimal.toLocaleString(undefined, {
                maximumFractionDigits: 2,
              })}{" "}
              {UNIT_OF_MASS} TS pro Tier.
            </div>
            <div>
              Das macht insg.{" "}
              {totalFreshMassPerAnimal.toLocaleString(undefined, {
                maximumFractionDigits: 2,
              })}{" "}
              {UNIT_OF_MASS} Frischmasse pro Tier.
            </div>
          </div>
        </>
      )}
    </>
  )
}

interface RecipePortionsEntryProps {
  org: OrganizationDto
  portion: Record<"id", string> & RecipePortionDto
  index: number
  remove: UseFieldArrayRemove
  parentRecipeName: string
}
function RecipePortionsEntry({
  portion,
  index,
  remove,
}: RecipePortionsEntryProps) {
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext<RecipeDto>()

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: portion.id })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <>
      <TableRow key={portion.id} style={style} ref={setNodeRef} {...attributes}>
        <TableCell>
          <FaGripLines className="inline" {...listeners} />
        </TableCell>
        <TableCell>
          <input
            {...register(`portions.${index}.name`, {
              required: "Geben Sie den Namen der Portion an.",
              maxLength: {
                value: RecipeSchemaNameMaxLength,
                message: `Der Name darf max. ${RecipeSchemaNameMaxLength} Zeichen lang sein.`,
              },
            })}
            className={errors.portions?.[index]?.name ? "error" : ""}
            placeholder="Portionsname"
          />
        </TableCell>
        <TableCell>
          <Controller
            control={control}
            name={`portions.${index}.percentage`}
            render={({ field, fieldState: { error } }) => (
              <input
                {...field}
                className={"w-20" + (error ? " error" : "")}
                onBlur={(e) => {
                  field.onChange(transformInputToNumber(e.target.value))
                  field.onBlur()
                }}
                value={transformNumberToString(field.value ?? 0)}
              />
            )}
            rules={{
              required: "Geben Sie einen Prozentsatz ein.",
              min: {
                value: 0.01,
                message: "Prozentsatz muss größer als 0 sein.",
              },
            }}
          />
          {" %"}
          <input
            type="hidden"
            {...register(`portions.${index}.order`)}
            value={index + 1}
          />
        </TableCell>
        <TableCell>
          <Button
            variation="warning"
            className="!border-none"
            onClick={() => remove(index)}
          >
            <span role="img" aria-label="remove">
              <FaRegTrashCan />
            </span>
          </Button>
        </TableCell>
      </TableRow>
      {errors.portions?.[index] && (
        <TableRow className="error">
          <TableCell colSpan={4}>
            {Object.entries(
              errors.portions[index] as FieldErrors<RecipePortionDto>
            ).map(([fieldName, error]) => (
              <div key={portion.id + "-errors-" + fieldName}>
                Fehler: {error.message}
              </div>
            ))}
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

export default function RecipeCreateEdit() {
  const params = useParams()
  const navigate = useNavigate()
  const { userData, reloadUserDataAsync } = useContext(UserDataContext)
  const [isUserDataFresh, setIsUserDataFresh] = useState(false)
  const [showArchiveModal, setShowArchiveModal] = useState(false)
  const [isFormSubmitting, setIsFormSubmitting] = useState(false)

  useEffect(() => {
    const ensureFreshUserData = async () => {
      if (isUserDataFresh) {
        console.log("ensureFreshUserData: User data has been reloaded")
      } else {
        console.log("ensureFreshUserData: User data will be reloaded ...")
        await reloadUserDataAsync()
        setIsUserDataFresh(true)
      }
    }
    ensureFreshUserData()
  }, [isUserDataFresh])

  const isReadOnly = useIsReadOnly()
  const isCreateMode = window.location.pathname.endsWith("/new")
  const isEditMode =
    window.location.pathname.endsWith("/edit") &&
    params["orgId"] &&
    params["recipeId"]
  const componentMode: ComponentModes = isCreateMode
    ? "create"
    : isEditMode
      ? "edit"
      : "invalid"
  const modalCallback = async (value: string) => {
    console.log("> modalCallback:", value)
    if (value.trim().toLowerCase() === "ja") {
      console.log("wanna delete: " + recipe.id)
      await deleteRecipe(recipe.id)
      /** @todo set global success message with data from `recipe` */

      console.log("Invalidating Cache")
      const cacheKey = "frontendApi#/users/current"
      await Cache.removeItem(cacheKey)

      if (reloadUserDataAsync) {
        console.log("Reloading data")
        await reloadUserDataAsync()
      } else {
        console.warn("Not reloading data; callback not set")
      }

      console.log("Navigating back")
      navigate(getLinkToOrgRecipes(org))
    } else {
      console.log("NO wanna delete")
    }
    setShowArchiveModal(false)
  }

  const org = UserDataContextHelper.findOrg(params["orgId"]!, userData!)
  const recipeOrig: RecipeDto = isEditMode
    ? UserDataContextHelper.findRecipe(org, params["recipeId"]!)
    : getEmptyRecipe(org)
  // Do clone magic to make rendering idempotent.
  // Could be replaced by fancy form field value conversion.
  const recipe = { ...recipeOrig }
  recipe.RecipeComponents = recipeOrig.RecipeComponents.map((elem) => {
    // Mixing time in the backend and DTO is in seconds, but this form uses minutes.
    // round with one fract. digit
    let mixing_time_s = elem.mixing_time_s
      ? Math.round((elem.mixing_time_s * 10) / 60) / 10
      : undefined
    const newElem = {
      ...elem,
      mixing_time_s,
    }
    if (undefined === mixing_time_s) {
      /** @ts-ignore */
      delete newElem.mixing_time_s
    }
    return newElem
  })

  const methods = useForm<RecipeDto>({
    values: recipe,
    mode: "onBlur",
  })
  const {
    formState: { errors },
    handleSubmit,
    register,
    setError,
  } = methods

  if (componentMode === "invalid") {
    return <div>Errrr... invalid component mode</div>
  }

  const onSubmit: SubmitHandler<RecipeDto> = async (data) => {
    console.log("▶️ onSubmit")
    if (isReadOnly) {
      console.log("🚧 Read-only mode.")
      setError("root.clientError", {
        type: "ui.read-only",
        message: "Bearbeiten ist nicht erlaubt.",
      })
      return
    }
    data.org_id = org.id
    setIsFormSubmitting(true)
    const cleaned = cleanData(data, isCreateMode)
    // console.log('clean', clean)
    console.log(
      "\n" +
        `${cleaned.name}\n` +
        `  Komponenten\n` +
        cleaned.RecipeComponents.map((rco) => {
          const fcId = rco.feed_component_id
          const fc = UserDataContextHelper.findFeedComponent(org, fcId)
          return `    ${fc.name}\t${rco.mass_kg} ${UNIT_OF_MASS}\t${rco.mixing_time_s} s`
        }).join("\n") +
        "\n" +
        `  Ausladegruppen\n` +
        cleaned.RecipesAusladegruppen.map((rag) => {
          const agId = rag.ausladegruppe_id
          const ag = UserDataContextHelper.findAnimalGroup(org, agId)
          return `    ${ag.name}\t${ag.number_of_animals} T`
        }).join("\n") +
        "\n" +
        `  Portionen\n` +
        cleaned.portions
          ?.map((p) => {
            return `    ${p.name}\tID ${p.id}`
          })
          .join("\n")
    )
    /** @todo Implement proper client-side validation (different for 'create' and 'update') */
    let validated: RecipeCreateDto | RecipeUpdateDto | undefined = undefined
    if (isCreateMode) {
      /** @todo: strip unknown properties, maybe with schema.cast() */
      if (RecipeCreateSchema.isValidSync(cleaned /*{ stripUnknown: true }*/)) {
        console.log("✅ Cleaned data is valid")
        // console.log("➡️ cleaned", cleaned)
        validated = cleaned
      } else {
        console.warn("❌ Cleaned data is not valid")
        try {
          validated = await RecipeCreateSchema.validate(cleaned, {
            abortEarly: false,
          })
        } catch (err) {
          /** @ts-ignore */
          console.log("Validation errors", err.errors)
        }
      }
    } else {
      /** @todo: strip unknown properties, maybe with schema.cast() */
      if (RecipeUpdateSchema.isValidSync(cleaned /*{ stripUnknown: true }*/)) {
        console.log("✅ Cleaned data is valid")
        // console.log("➡️ cleaned", cleaned)
        validated = cleaned
      } else {
        console.warn("❌ Cleaned data is not valid")
        try {
          validated = await RecipeUpdateSchema.validate(cleaned, {
            abortEarly: false,
          })
        } catch (err) {
          /** @ts-ignore */
          console.log("Validation errors", err.errors)
        }
      }
    }
    if (!validated) {
      setIsFormSubmitting(false)
      return false
    }
    try {
      const result = isEditMode
        ? await updateRecipe(recipe.id, cleaned)
        : await createRecipe(cleaned)

      console.log("Invalidating Cache")
      const cacheKey = "frontendApi#/users/current"
      await Cache.removeItem(cacheKey)

      if (reloadUserDataAsync) {
        console.log("Reloading data")
        await reloadUserDataAsync()
      } else {
        console.warn("Not reloading data; callback not set")
      }

      setIsFormSubmitting(false)
      console.log("Navigating back")
      navigate(getLinkToOrgRecipes(org))

      return result
    } catch (error) {
      console.error("Failed to save recipe:", error)
      const eAsAny = error as any
      setError("root.serverError", {
        type: eAsAny.statusCode,
        message: isProd
          ? "Fehler beim Speichern des Rezepts."
          : "Fehler beim Speichern des Rezepts: " +
            getErrorMessage(eAsAny.body),
      })
      setIsFormSubmitting(false)
    }
    return
  }

  async function createRecipe(data: RecipeCreateDto): Promise<ResponseMessage> {
    console.log("> createRecipe")
    const { json, response } = await postToApi("frontendApi", `/recipes`, data)
    console.log("Got API response:", response)
    return json
  }
  async function updateRecipe(
    recipeId: string,
    data: RecipeUpdateDto
  ): Promise<ResponseMessage> {
    console.log("> updateRecipe")
    const { json, response } = await putToApi(
      "frontendApi",
      `/recipes/${recipeId}`,
      data
    )
    console.log("Got API response:", response)
    return json
  }
  async function deleteRecipe(recipeId: string) {
    console.log("> deleteRecipe", recipeId)
    const { response } = await delToApi("frontendApi", `/recipes/${recipeId}`)
    const statusCode: number = response.statusCode
    console.log("Got API response with statusCode:", statusCode)
    console.log("Got API response:", response)
    return statusCode
  }

  const imageIdx = imageHash(IMAGE_HASH_SEED, recipe.id, NUM_RC_IMAGES)
  const imageSrc = `/images/recipes/ration-pixabay-${imageIdx}.jpg`

  return (
    <>
      <div className="rm-edit-page">
        <div
          className="hdr-img"
          style={{ backgroundImage: `url('${imageSrc}')` }}
        />
        <div className="content-overlay">
          <div className="header">
            <div className="info">
              <h2>{recipe.name}</h2>
            </div>
          </div>

          <div className="details">
            {/* pass all methods into the context */}
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Flex direction="column" gap="small">
                  <Flex className="field-row">
                    <Label htmlFor="name">Rezeptname:</Label>
                    <input
                      id="name"
                      {...register("name", {
                        required: "Geben Sie den Namen des Rezepts an.",
                        maxLength: {
                          value: RecipeSchemaNameMaxLength,
                          message: `Der Name darf max. ${RecipeSchemaNameMaxLength} Zeichen lang sein.`,
                        },
                      })}
                      placeholder={recipe.name || "Neues Rezept"}
                      className={errors?.name ? "error" : ""}
                    />
                    {errors?.name ? "Fehler: " + errors?.name.message : ""}
                  </Flex>

                  <RecipeComponentsList org={org} />

                  <RecipeAusladegruppen org={org} />

                  <RecipePortions org={org} parentRecipeName={recipe.name} />

                  {errors?.root && errors?.root["clientError"] && (
                    <p className="error">
                      {errors.root["clientError"].message}
                    </p>
                  )}
                  {errors?.root && errors?.root["serverError"] && (
                    <p className="error">
                      {errors.root["serverError"].message}
                    </p>
                  )}

                  <div className="buttons">
                    <Button
                      type="submit"
                      variation="primary"
                      isDisabled={isFormSubmitting || isReadOnly}
                      isLoading={isFormSubmitting}
                      loadingText={I18n.get("Wird gespeichert...")}
                    >
                      Speichern
                    </Button>
                    <CancelButton
                      href=".."
                      title="Abbrechen"
                      isDisabled={isFormSubmitting}
                    />
                    {componentMode === "edit" && (
                      <>
                        <Button
                          isDisabled={isFormSubmitting || isReadOnly}
                          variation="warning"
                          onClick={() => {
                            setShowArchiveModal(true)
                          }}
                        >
                          Archivieren
                        </Button>
                        <Modal
                          buttons={[
                            {
                              text: "Ja",
                              variation: "destructive",
                            },
                            { text: "Nein" },
                          ]}
                          show={showArchiveModal}
                          callback={modalCallback}
                        >
                          <div className="title">
                            Rezept wirklich archivieren?
                          </div>
                          <div>
                            Wollen Sie das Rezept "{recipe.name}" wirklich
                            archivieren?
                          </div>
                          <div>
                            Wenn ein Eintrag archiviert ist, kann er nicht mehr
                            bearbeitet werden, und wird nicht mehr zu den
                            Maschinen übertragen.
                            <br />
                            In Statistiken taucht er weiterhin auf.
                          </div>
                        </Modal>
                      </>
                    )}
                  </div>
                </Flex>
              </form>
            </FormProvider>

            {/* {componentMode === "edit"
            &&
            <div className="mt-5 text-xs">
              <div>Zuletzt bearbeitet am {recipe.updated_at} von {recipe.updated_by}</div>
              <div>Angelegt am {recipe.created_at} von {recipe.created_by}</div>
            </div>
          } */}
          </div>
        </div>
      </div>

      {/* <small>* Die Icons "xmark (light)", "pen-to-square (light)" benötigen FontAwesome Pro, kostet 49,- oder 99,- pro Jahr.</small> */}
    </>
  )
}
